import type { Language, TranslationModule, FullTranslationDictionary, NamespaceBundle } from './types';
import { commonTranslations } from './modules/common';
import { headerTranslations } from './modules/header';
import { footerTranslations } from './modules/footer';
import { prezentareTranslations } from './modules/prezentare';
import { searchTranslations } from './modules/search';
import { mainContentTranslations } from './modules/mainContent';
import { modulesTranslations } from './modules/modules';
import { sidebarTranslations } from './modules/sidebar';
import { usefulLinksTranslations } from './modules/usefulLinks';
import { crmSectionTranslations } from './modules/crmSection';
import { trackCasesSectionTranslations } from './modules/trackCasesSection';
import { cedoSectionTranslations } from './modules/cedoSection';
import { iccjSectionTranslations } from './modules/iccjSection';
import { infoLegalTranslations } from './modules/infoLegal';
import { faqTranslations } from './modules/faq';
import { contactTranslations } from './modules/contact';
import { aboutTranslations } from './modules/about';
import { termsTranslations } from './modules/terms';
import { privacyTranslations } from './modules/privacy';
import { cookiesTranslations } from './modules/cookies';
import { gdprTranslations } from './modules/gdpr';
import { authTranslations } from './modules/auth';
import { quickServicesTranslations } from './modules/quickServices';
import { companyFormationTranslations } from './modules/companyFormation';
import { extrasCfTranslations } from './modules/extrasCf';
import { lawyerAssistanceTranslations } from './modules/lawyerAssistance';
import { roiTranslations } from './modules/roi';
import { abonamenteTranslations } from './modules/abonamente';
import { settingsTranslations } from './modules/settings';
import { ecrisTranslations } from './modules/ecris';
import { tickerTranslations } from './modules/ticker';
import { perspectiveTranslations } from './modules/perspective';
import { uploadDrawerTranslations } from './modules/uploadDrawer';
import { homeTranslations } from './modules/home';
import { publicCasesTranslations } from './modules/publicCases';
import { ghidTranslations } from './modules/ghid';
import { practicaNeunitaraTranslations } from './modules/practicaNeunitara';
import { jurnalPracticaJudiciaraTranslations } from './modules/jurnalPracticaJudiciara';
import { financeTranslations } from './modules/finance';
import { caseCalendarTranslations } from './modules/caseCalendar';
import { documentEditorTranslations } from './modules/documentEditor';
import { dosareUrmariteTranslations } from './modules/dosareUrmarite';
import { legislativeChangesTranslations } from './modules/legislativeChanges';

export const staticModules: Record<string, TranslationModule> = {
  common: commonTranslations,
  documentEditor: documentEditorTranslations,
  dosareUrmarite: dosareUrmariteTranslations,
  legislativeChanges: legislativeChangesTranslations,
  finance: financeTranslations,
  header: headerTranslations,
  footer: footerTranslations,
  prezentare: prezentareTranslations,
  search: searchTranslations,
  mainContent: mainContentTranslations,
  modules: modulesTranslations,
  sidebar: sidebarTranslations,
  usefulLinks: usefulLinksTranslations,
  crmSection: crmSectionTranslations,
  trackCasesSection: trackCasesSectionTranslations,
  cedoSection: cedoSectionTranslations,
  iccjSection: iccjSectionTranslations,
  infoLegal: infoLegalTranslations,
  faq: faqTranslations,
  contact: contactTranslations,
  about: aboutTranslations,
  terms: termsTranslations,
  privacy: privacyTranslations,
  cookies: cookiesTranslations,
  gdpr: gdprTranslations,
  auth: authTranslations,
  quickServices: quickServicesTranslations,
  companyFormation: companyFormationTranslations,
  extrasCf: extrasCfTranslations,
  lawyerAssistance: lawyerAssistanceTranslations,
  roi: roiTranslations,
  abonamente: abonamenteTranslations,
  settings: settingsTranslations,
  ecris: ecrisTranslations,
  ticker: tickerTranslations,
  perspective: perspectiveTranslations,
  uploadDrawer: uploadDrawerTranslations,
  home: homeTranslations,
  publicCases: publicCasesTranslations,
  ghid: ghidTranslations,
  practicaNeunitara: practicaNeunitaraTranslations,
  jurnalPracticaJudiciara: jurnalPracticaJudiciaraTranslations,
  caseCalendar: caseCalendarTranslations,
};

const dynamicModules: Record<string, TranslationModule> = {};

export function registerNamespace(namespace: string, module: TranslationModule): void {
  dynamicModules[namespace] = module;
}

export function getRegisteredModule(namespace: string): TranslationModule | undefined {
  return dynamicModules[namespace] || staticModules[namespace];
}

export function getLanguageDictionary(lang: Language): NamespaceBundle {
  const bundle: NamespaceBundle = {};
  for (const [ns, mod] of Object.entries(staticModules)) {
    bundle[ns] = mod[lang] || mod['ro'] || {};
  }
  for (const [ns, mod] of Object.entries(dynamicModules)) {
    bundle[ns] = mod[lang] || mod['ro'] || {};
  }
  return bundle;
}

export function getTranslations(): FullTranslationDictionary {
  return {
    ro: getLanguageDictionary('ro'),
    en: getLanguageDictionary('en'),
    fr: getLanguageDictionary('fr')
  };
}

export function resolveTranslation(
  lang: Language,
  keyPath: string,
  variables?: Record<string, unknown>
): string {
  const parts = keyPath.split('.');
  const [namespace, ...rest] = parts;
  if (!namespace) return keyPath;

  const mod = getRegisteredModule(namespace);
  if (!mod) return keyPath;

  const lookup = (set: Record<string, unknown> | undefined): string | undefined => {
    let cur: unknown = set;
    for (const part of rest) {
      if (cur == null || typeof cur !== 'object') return undefined;
      cur = (cur as Record<string, unknown>)[part];
    }
    return typeof cur === 'string' ? cur : undefined;
  };

  // 1. Try active language
  let text = lookup(mod[lang]);

  // 2. Fallback to Romanian
  if (text === undefined && lang !== 'ro') {
    text = lookup(mod['ro']);
  }

  // 3. Fallback to keyPath
  if (text === undefined) {
    return keyPath;
  }

  // 4. Interpolate variables e.g. {count}
  if (variables) {
    for (const [k, v] of Object.entries(variables)) {
      text = text.replace(new RegExp(`{${k}}`, 'g'), String(v));
    }
  }

  return text;
}
